import React, { useState, useEffect, useMemo } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import Swal from 'sweetalert2'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import usePagination from '../../hooks/usePagination'
import TablePagination from '../../Components/common/TablePagination'

const AccessLogs = () => {
  const [logs, setLogs] = useState([])
  const [filteredLogs, setFilteredLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState(null)
  const [filters, setFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    user: '',
    action: ''
  })

  // Format timestamp from ISO format to readable format
  const formatTimestamp = (isoString) => {
    if (!isoString) return ''
    try {
      const date = new Date(isoString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
    } catch (error) {
      return isoString
    }
  }

  // Format activity type for display
  const formatActivityType = (activityType) => {
    if (!activityType) return 'Unknown'
    return activityType.charAt(0).toUpperCase() + activityType.slice(1).toLowerCase()
  }

  // Extract username from email (part before @)
  const getUsernameFromEmail = (email) => {
    if (!email) return 'Unknown'
    return email.split('@')[0]
  }

  useEffect(() => {
    const fetchAccessLogs = async () => {
      try {
        setLoading(true)
        const response = await apiClient.get(API_ENDPOINTS.AUDIT_LOGS)
        const logsData = Array.isArray(response.data) ? response.data : response.data?.results || []
        
        // Map API response to component format
        const mappedLogs = logsData.map(log => ({
          id: log.id,
          raw: log,
          user: getUsernameFromEmail(log.user_email),
          email: log.user_email,
          action: formatActivityType(log.activity_type),
          ip_address: log.ip_address || 'N/A',
          timestamp: formatTimestamp(log.timestamp),
          rawTimestamp: log.timestamp, // Keep raw timestamp for date filtering
          description: log.description,
          user_agent: log.user_agent,
          metadata: log.metadata,
          related_model: log.related_model,
          related_object_id: log.related_object_id
        }))
        
        setLogs(mappedLogs)
        setFilteredLogs(mappedLogs)
      } catch (error) {
        console.error('Error fetching access logs:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.detail || error.response?.data?.message || 'Failed to fetch access logs. Please try again.',
          confirmButtonColor: '#d33'
        })
        setLogs([])
        setFilteredLogs([])
      } finally {
        setLoading(false)
      }
    }

    fetchAccessLogs()
  }, [])

  // Get unique users from logs for the filter dropdown
  const uniqueUsers = useMemo(() => {
    const userSet = new Set()
    logs.forEach(log => {
      if (log.email) {
        userSet.add(log.email)
      }
    })
    return Array.from(userSet).sort()
  }, [logs])

  // Get unique actions from logs for the filter dropdown
  const uniqueActions = useMemo(() => {
    const actionSet = new Set()
    logs.forEach(log => {
      if (log.action) {
        actionSet.add(log.action)
      }
    })
    return Array.from(actionSet).sort()
  }, [logs])

  useEffect(() => {
    let filtered = [...logs]

    if (filters.user) {
      filtered = filtered.filter(log => log.email === filters.user)
    }

    if (filters.action) {
      const actionLower = filters.action.toLowerCase()
      filtered = filtered.filter(log => log.action?.toLowerCase() === actionLower)
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(log =>
        log.user?.toLowerCase().includes(searchLower) ||
        log.email?.toLowerCase().includes(searchLower) ||
        log.action?.toLowerCase().includes(searchLower) ||
        log.ip_address?.includes(searchLower) ||
        log.description?.toLowerCase().includes(searchLower)
      )
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(log => {
        if (!log.rawTimestamp) return false
        const logDate = new Date(log.rawTimestamp).toISOString().split('T')[0]
        return logDate >= filters.dateFrom
      })
    }

    if (filters.dateTo) {
      filtered = filtered.filter(log => {
        if (!log.rawTimestamp) return false
        const logDate = new Date(log.rawTimestamp).toISOString().split('T')[0]
        return logDate <= filters.dateTo
      })
    }

    setFilteredLogs(filtered)
  }, [logs, filters])

  const {
    paginatedItems: paginatedLogs,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    totalPages,
    startIndex,
    endIndex
  } = usePagination(filteredLogs)

  // Generate PDF from filtered logs
  const generatePDF = () => {
    if (filteredLogs.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Data',
        text: 'There are no logs to export. Please adjust your filters.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    try {
      const doc = new jsPDF('landscape', 'mm', 'a4')
      
      // Add title
      doc.setFontSize(18)
      doc.setTextColor(40, 40, 40)
      doc.text('Access Logs Report', 14, 15)
      
      // Add filters information
      doc.setFontSize(10)
      doc.setTextColor(100, 100, 100)
      let yPos = 22
      const filtersApplied = []
      if (filters.search) filtersApplied.push(`Search: ${filters.search}`)
      if (filters.user) filtersApplied.push(`User: ${filters.user}`)
      if (filters.action) filtersApplied.push(`Action: ${filters.action}`)
      if (filters.dateFrom) filtersApplied.push(`From: ${filters.dateFrom}`)
      if (filters.dateTo) filtersApplied.push(`To: ${filters.dateTo}`)
      
      if (filtersApplied.length > 0) {
        doc.text(`Filters Applied: ${filtersApplied.join(' | ')}`, 14, yPos)
        yPos += 5
      }
      
      doc.text(`Generated: ${new Date().toLocaleString()} | Total Records: ${filteredLogs.length}`, 14, yPos)
      yPos += 8
      
      // Prepare table data
      const tableData = filteredLogs.map(log => [
        log.id,
        log.user,
        log.email,
        log.action,
        log.ip_address,
        log.timestamp,
        log.description || 'N/A'
      ])
      
      // Generate table
      doc.autoTable({
        startY: yPos,
        head: [['ID', 'User', 'Email', 'Action', 'IP Address', 'Timestamp', 'Description']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [66, 139, 202],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 9
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [40, 40, 40]
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250]
        },
        columnStyles: {
          0: { cellWidth: 15 }, // ID
          1: { cellWidth: 25 }, // User
          2: { cellWidth: 40 }, // Email
          3: { cellWidth: 25 }, // Action
          4: { cellWidth: 30 }, // IP Address
          5: { cellWidth: 40 }, // Timestamp
          6: { cellWidth: 60 }  // Description
        },
        margin: { top: yPos, left: 14, right: 14 },
        styles: {
          overflow: 'linebreak',
          cellPadding: 2
        }
      })
      
      // Add page numbers
      const pageCount = doc.internal.getNumberOfPages()
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.setFontSize(8)
        doc.setTextColor(150, 150, 150)
        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.getWidth() - 30,
          doc.internal.pageSize.getHeight() - 10
        )
      }
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0]
      const filename = `access-logs-${timestamp}.pdf`
      
      // Save PDF
      doc.save(filename)
      
      Swal.fire({
        icon: 'success',
        title: 'PDF Generated',
        text: `Successfully exported ${filteredLogs.length} log(s) to PDF.`,
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      })
    } catch (error) {
      console.error('Error generating PDF:', error)
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: 'An error occurred while generating the PDF. Please try again.',
        confirmButtonColor: '#d33'
      })
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Access Logs</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Home</li>
            <li className="breadcrumb-item">Users</li>
            <li className="breadcrumb-item active">Access Logs</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-clock-history me-2"></i>
                  Access Logs
                </h5>

                {/* Filters */}
                <div className="row g-2 mb-3">
                  <div className="col-md-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search logs..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    />
                  </div>
                  <div className="col-md-3">
                    <select
                      className="form-select"
                      value={filters.user}
                      onChange={(e) => setFilters({ ...filters, user: e.target.value })}
                    >
                      <option value="">All Users</option>
                      {uniqueUsers.map(email => (
                        <option key={email} value={email}>
                          {email}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <select
                      className="form-select"
                      value={filters.action}
                      onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                    >
                      <option value="">All Actions</option>
                      {uniqueActions.map(action => (
                        <option key={action} value={action}>
                          {action}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <input
                      type="date"
                      className="form-control"
                      placeholder="From Date"
                      value={filters.dateFrom}
                      onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    />
                  </div>
                  <div className="col-md-2">
                    <input
                      type="date"
                      className="form-control"
                      placeholder="To Date"
                      value={filters.dateTo}
                      onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                    />
                  </div>
                  <div className="col-md-2 col-sm-4 col-12 d-flex align-items-end gap-2">
                    <button
                      className="btn btn-outline-secondary btn-sm flex-fill"
                      onClick={() => setFilters({ search: '', dateFrom: '', dateTo: '', user: '', action: '' })}
                    >
                      {/* <i className="bi bi-x-circle me-1"></i> */}
                      Clear Filters
                    </button>
                    <button
                      className="btn btn-danger btn-sm flex-fill"
                      onClick={generatePDF}
                      disabled={filteredLogs.length === 0}
                      title="Export filtered logs to PDF"
                      style={{
                        background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                        border: 'none',
                        boxShadow: '0 2px 4px rgba(220, 53, 69, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)'
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 53, 69, 0.4)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 53, 69, 0.3)'
                      }}
                    >
                      {/* <i className="bi bi-file-earmark-pdf me-1"></i> */}
                      Export PDF
                    </button>
                  </div>
                </div>

                {/* Logs Table */}
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : filteredLogs.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted">No access logs found.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>User</th>
                          <th>Email</th>
                          <th>Action</th>
                          <th>IP Address</th>
                          <th>Timestamp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedLogs.map(log => (
                          <React.Fragment key={log.id}>
                            <tr
                              onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td>{log.id}</td>
                              <td>{log.user}</td>
                              <td>{log.email}</td>
                              <td>
                                <span className={`badge ${log.action.toLowerCase() === 'login' ? 'bg-success' : 'bg-info'}`}>
                                  {log.action}
                                </span>
                              </td>
                              <td>{log.ip_address}</td>
                              <td>
                                <div className="d-flex align-items-center justify-content-between">
                                  <span>{log.timestamp}</span>
                                  <i
                                    className={`bi ms-2 ${expandedId === log.id ? 'bi-chevron-up' : 'bi-chevron-down'}`}
                                  ></i>
                                </div>
                              </td>
                            </tr>
                            {expandedId === log.id && (
                              <tr className="table-active">
                                <td colSpan={6}>
                                  <div className="p-3">
                                    <div className="row mb-2">
                                      <div className="col-md-6">
                                        <strong>Description:</strong>
                                        <div className="text-muted small mt-1">
                                          {log.description || 'No description available.'}
                                        </div>
                                      </div>
                                      <div className="col-md-6">
                                        <strong>IP Address:</strong>
                                        <div className="text-muted small mt-1">
                                          {log.ip_address || 'N/A'}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="row mb-2">
                                      <div className="col-md-6">
                                        <strong>User Agent:</strong>
                                        <div className="text-muted small mt-1">
                                          {log.user_agent || 'N/A'}
                                        </div>
                                      </div>
                                      <div className="col-md-6">
                                        <strong>Related Object:</strong>
                                        <div className="text-muted small mt-1">
                                          {log.related_model
                                            ? `${log.related_model} (ID: ${log.related_object_id ?? 'N/A'})`
                                            : 'None'}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="row">
                                      <div className="col-12">
                                        <strong>Metadata:</strong>
                                        <pre className="bg-light border rounded small p-2 mt-1 mb-0">
                                          {log.metadata
                                            ? JSON.stringify(log.metadata, null, 2)
                                            : 'No metadata'}
                                        </pre>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                    <TablePagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={totalItems}
                      startIndex={startIndex}
                      endIndex={endIndex}
                      pageSize={pageSize}
                      onPageChange={setCurrentPage}
                      onPageSizeChange={setPageSize}
                      itemLabel="logs"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default AccessLogs

